'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';

interface ChangelogEntry {
  id: string;
  toolSlug: string;
  toolName: string;
  date: string;
  type: 'release' | 'update' | 'pricing' | 'feature' | 'breaking' | 'acquisition';
  title: string;
  description: string;
  version?: string;
  impact: 'major' | 'minor' | 'patch';
  source: string;
  tool?: any; // Pre-processed tool data from server component
}

interface ChangelogFilterProps {
  entries: ChangelogEntry[];
}

function getImpactColor(impact: string): string {
  switch (impact) {
    case 'major':
      return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
    case 'minor':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
    case 'patch':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
}

function getTypeColor(type: string): string {
  switch (type) {
    case 'release':
      return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
    case 'feature':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300';
    case 'update':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
    case 'pricing':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300';
    case 'breaking':
      return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
    case 'acquisition':
      return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
}

function getTypeIcon(type: string): string {
  switch (type) {
    case 'release':
      return '🚀';
    case 'feature':
      return '✨';
    case 'update':
      return '🔄';
    case 'pricing':
      return '💰';
    case 'breaking':
      return '⚠️';
    case 'acquisition':
      return '🤝';
    default:
      return '📝';
  }
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function ChangelogEntry({ entry }: { entry: ChangelogEntry }) {
  const tool = entry.tool;

  return (
    <div className="relative pl-8 pb-8 last:pb-0">
      {/* Timeline line */}
      <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700"></div>
      
      {/* Timeline dot */}
      <div className="absolute left-0 top-2 w-2 h-2 bg-emerald-500 rounded-full transform -translate-x-0.75"></div>
      
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{getTypeIcon(entry.type)}</span>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  {entry.title}
                </h2>
                {entry.version && (
                  <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded">
                    v{entry.version}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {tool ? (
                  <Link 
                    href={`/tools/${entry.toolSlug}`}
                    className="text-emerald-600 dark:text-emerald-400 hover:underline font-medium"
                  >
                    {entry.toolName}
                  </Link>
                ) : (
                  <span className="font-medium text-gray-900 dark:text-white">
                    {entry.toolName}
                  </span>
                )}
                <span className="text-gray-400 dark:text-gray-500">•</span>
                <span className="text-gray-500 dark:text-gray-400 text-sm">
                  {formatDate(entry.date)}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col gap-1">
            <span className={`px-2 py-1 rounded text-xs font-semibold ${getTypeColor(entry.type)}`}>
              {entry.type}
            </span>
            <span className={`px-2 py-1 rounded text-xs font-semibold ${getImpactColor(entry.impact)}`}>
              {entry.impact}
            </span>
          </div>
        </div>
        
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {entry.description}
        </p>
        
        <div className="flex items-center justify-between">
          <a 
            href={entry.source}
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-600 dark:text-emerald-400 hover:underline text-sm flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            View Source
          </a>
        </div>
      </div>
    </div>
  );
}

export default function ChangelogFilter({ entries }: ChangelogFilterProps) {
  const [filters, setFilters] = useState({
    type: '',
    impact: '',
    search: '',
  });

  const filteredEntries = useMemo(() => {
    return entries.filter(entry => {
      const matchesType = !filters.type || entry.type === filters.type;
      const matchesImpact = !filters.impact || entry.impact === filters.impact;
      const matchesSearch = !filters.search || 
        entry.toolName.toLowerCase().includes(filters.search.toLowerCase()) ||
        (entry.title || '').toLowerCase().includes(filters.search.toLowerCase()) ||
        (entry.description || '').toLowerCase().includes(filters.search.toLowerCase());
      
      return matchesType && matchesImpact && matchesSearch;
    });
  }, [entries, filters]);

  const clearFilters = () => {
    setFilters({ type: '', impact: '', search: '' });
  };

  const hasActiveFilters = filters.type || filters.impact || filters.search;

  return (
    <>
      {/* Filter Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* Search */}
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Search tools
            </label>
            <input
              type="text"
              id="search"
              placeholder="Search by tool name..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          {/* Type Filter */}
          <div>
            <label htmlFor="type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Update Type
            </label>
            <select
              id="type"
              value={filters.type}
              onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="">All types</option>
              <option value="release">🚀 Releases</option>
              <option value="feature">✨ Features</option>
              <option value="update">🔄 Updates</option>
              <option value="pricing">💰 Pricing</option>
              <option value="breaking">⚠️ Breaking</option>
              <option value="acquisition">🤝 Acquisitions</option>
            </select>
          </div>

          {/* Impact Filter */}
          <div>
            <label htmlFor="impact" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Impact Level
            </label>
            <select
              id="impact"
              value={filters.impact}
              onChange={(e) => setFilters(prev => ({ ...prev, impact: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="">All impacts</option>
              <option value="major">Major</option>
              <option value="minor">Minor</option>
              <option value="patch">Patch</option>
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Showing {filteredEntries.length} of {entries.length} updates
          </p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-emerald-600 dark:text-emerald-400 hover:underline text-sm"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="mt-8">
        <div className="max-w-3xl">
          {filteredEntries.map(entry => (
            <ChangelogEntry key={entry.id} entry={entry} />
          ))}
          
          {filteredEntries.length === 0 && (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No updates found</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Try adjusting your filters to see more results.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}